// src/stores/ContentStore.ts - Updated với Strapi v5 documentId support
// ✅ GIỮ NGUYÊN TẤT CẢ FUNCTIONS KHÁC - CHỈ SỬA CÁC OPERATIONS DELETE/UPDATE

import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from './index';
import contentService from '@/services/contentService';
import { v4 as uuidv4 } from 'uuid';
import { getDefaultFilters, ensureFilters, safeGet, calculateProtectionScore } from './utils/storeUtils';
import { ID } from '@/types';

export interface ContentFormData {
    title: string;
    description: string;
    contentKind: string; // ✅ Giữ nguyên contentKind cho compatibility
    category: string;
    industry?: string;
    files: File[];
    tags: string[];
    isPublic: boolean;
    protection: {
        watermark: boolean;
        aiMonitoring: boolean;
        blockchain: boolean;
    };
    collaborators: Collaborator[];
    termsAgreed: boolean;
    ownershipAgreed: boolean;
    dimensions?: string;
    material?: string;
}

export interface Collaborator {
    id: string;
    name: string;
    email: string;
    role: string;
    sharePercentage: number;
}

// ✅ Thêm interface cho MinIO tracking
export interface MinioFileInfo {
    name: string;
    path: string;
    url: string;
    size: number;
    mimeType: string;
    bucket: string;
}

// Mock data cho categories và tags
const MOCK_CATEGORIES = [
    { id: '1', name: 'Âm nhạc', attributes: { name: 'Âm nhạc' } },
    { id: '2', name: 'Văn học', attributes: { name: 'Văn học' } },
    { id: '3', name: 'Nghệ thuật', attributes: { name: 'Nghệ thuật' } },
    { id: '4', name: 'Nhiếp ảnh', attributes: { name: 'Nhiếp ảnh' } },
    { id: '5', name: 'Phim ảnh', attributes: { name: 'Phim ảnh' } },
    { id: '6', name: 'Phần mềm', attributes: { name: 'Phần mềm' } },
    { id: '7', name: 'Khác', attributes: { name: 'Khác' } }
];

const MOCK_TAGS = [
    { id: '1', name: 'Pop', attributes: { name: 'Pop' } },
    { id: '2', name: 'Rock', attributes: { name: 'Rock' } },
    { id: '3', name: 'Classical', attributes: { name: 'Classical' } },
    { id: '4', name: 'Tiểu thuyết', attributes: { name: 'Tiểu thuyết' } },
    { id: '5', name: 'Thơ', attributes: { name: 'Thơ' } },
    { id: '6', name: 'Sơn dầu', attributes: { name: 'Sơn dầu' } },
    { id: '7', name: 'Phong cảnh', attributes: { name: 'Phong cảnh' } },
    { id: '8', name: 'Chân dung', attributes: { name: 'Chân dung' } }
];

class ContentStore {
    rootStore: RootStore;
    contentFormData: ContentFormData = {
        title: '',
        description: '',
        contentKind: '', // ✅ Giữ nguyên contentKind
        category: '',
        files: [],
        tags: [],
        isPublic: false,
        protection: {
            watermark: true,
            aiMonitoring: true,
            blockchain: false
        },
        collaborators: [],
        termsAgreed: false,
        ownershipAgreed: false
    };

    categories: any[] = [];
    tags: any[] = [];
    isLoading: boolean = false;
    error: string | null = null;
    registerSuccess: boolean = false;
    currentStep: number = 1;
    totalSteps: number = 5;
    uploadProgress: number = 0;

    // Contents management - ✅ Giữ nguyên từ version cũ
    contents: any[] = [];
    userContent: any[] = []; // Danh sách content của user hiện tại
    totalCount: number = 0;
    page: number = 1;
    pageSize: number = 12;
    viewMode: 'grid' | 'list' = 'grid';

    filters: {
        searchTerm: string;
        contentKind: string;
        status: string;
        sortField: string;
        sortOrder: 'asc' | 'desc';
    } = getDefaultFilters();

    // ✅ Thêm MinIO tracking mới
    minioUploadStatus: {
        isUploading: boolean;
        uploadedFiles: MinioFileInfo[];
        totalSize: number;
    } = {
            isUploading: false,
            uploadedFiles: [],
            totalSize: 0
        };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });

        // ✅ Sửa lỗi: Ensure filters are initialized properly
        this.filters = ensureFilters(this.filters);
    }

    // ✅ Giữ nguyên tất cả methods cũ
    setError(error: string | null) {
        this.error = error;
    }

    setFormData(field: string, value: any) {
        // @ts-ignore
        this.contentFormData[field] = value;
    }

    setProtection(field: string, value: boolean) {
        this.contentFormData.protection = {
            ...this.contentFormData.protection,
            [field]: value
        };
    }

    setContentKind(type: string) {
        this.contentFormData.contentKind = type;
    }

    // ✅ Enhanced setFiles với validation
    setFiles(files: File[] = [], index?: number) {
        if(index){
            this.contentFormData.files = this.contentFormData.files.filter((_, i) => index !== i );
        } 
        
        // Basic validation
        const validFiles = files.filter(file => {
            const maxSize = 100 * 1024 * 1024; // 100MB
            return file.size <= maxSize;
        }) || [];

        if (validFiles.length !== files.length) {
            this.setError('Một số file quá lớn (tối đa 100MB) và đã bị loại bỏ');
        }

        this.contentFormData.files = [...this.contentFormData.files, ...validFiles];        

        // ✅ Update MinIO total size
        this.minioUploadStatus.totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    }

    addCollaborator() {
        const newCollaborator: Collaborator = {
            id: uuidv4(),
            name: '',
            email: '',
            role: 'contributor',
            sharePercentage: 0
        };
        this.contentFormData.collaborators.push(newCollaborator);
    }

    updateCollaborator(id: string, field: keyof Collaborator, value: string | number) {
        const index = this.contentFormData.collaborators.findIndex(c => c.id === id);
        if (index !== -1) {
            this.contentFormData.collaborators[index] = {
                ...this.contentFormData.collaborators[index],
                [field]: value
            };
        }
    }

    removeCollaborator(id: string) {
        this.contentFormData.collaborators = this.contentFormData.collaborators.filter(c => c.id !== id);
    }

    setCurrentStep(step: number) {
        this.currentStep = step;
    }

    nextStep() {
        if (this.currentStep < this.totalSteps) {
            this.currentStep++;
        }
    }

    prevStep() {
        if (this.currentStep > 1) {
            this.currentStep--;
        }
    }

    setUploadProgress(progress: number) {
        this.uploadProgress = progress;
    }

    // Categories và Tags management - ✅ Giữ nguyên
    async fetchCategories() {
        this.isLoading = true;
        this.error = null;

        try {
            const categories = await contentService.getCategories();
            runInAction(() => {
                this.categories = categories;
                this.isLoading = false;
            });
        } catch (error: any) {
            console.warn('API call failed, using mock data for categories:', error.message);
            runInAction(() => {
                this.categories = MOCK_CATEGORIES;
                this.isLoading = false;
            });
        }
    }

    async fetchTags() {
        this.isLoading = true;
        this.error = null;

        try {
            const tags = await contentService.getTags();
            runInAction(() => {
                this.tags = tags;
                this.isLoading = false;
            });
        } catch (error: any) {
            console.warn('API call failed, using mock data for tags:', error.message);
            runInAction(() => {
                this.tags = MOCK_TAGS;
                this.isLoading = false;
            });
        }
    }

    // ✅ Enhanced registerContent với MinIO support
    async registerContent() {
        try {
            this.isLoading = true;
            this.error = null;
            this.uploadProgress = 0;

            // Validation cơ bản
            if (!this.contentFormData.title.trim()) {
                throw new Error('errorTitleRequired');
                // throw new Error('Vui lòng nhập tiêu đề tác phẩm');
            }

            if (!this.contentFormData.contentKind) {
                throw new Error('errorContentKindRequired');
                // throw new Error('Vui lòng chọn loại nội dung');
            }

            if (!this.contentFormData.category) {
                throw new Error('errorCategoryRequired');
                // throw new Error('Vui lòng chọn danh mục');
            }

            try {
                // Thử gọi API trước với enhanced upload
                const { uploadContentToStrapi, getStrapiErrorMessage } = await import('@/utils/uploadHelpers');

                // ✅ Map contentKind -> contentKind cho API
                const dataPayload = {
                    title: this.contentFormData.title,
                    description: this.contentFormData.description,
                    contentKind: this.contentFormData.contentKind, // Map contentKind -> contentKind
                    category: this.contentFormData.category,
                    isPublic: this.contentFormData.isPublic,
                    status: 'pending',

                    tags: this.contentFormData.tags.map(id => ({ id })),

                    collaborators: this.contentFormData.collaborators.length > 0
                        ? this.contentFormData.collaborators
                        : [{
                            id: "owner",
                            name: this.rootStore.authStore?.currentUser?.firstName + ' ' + this.rootStore.authStore?.currentUser?.lastName || 'Chủ sở hữu',
                            email: this.rootStore.authStore?.currentUser?.email || 'owner@example.com',
                            role: "owner",
                            sharePercentage: 100
                        }],
                    ownershipDistribution: this.contentFormData.collaborators.length > 0
                        ? this.contentFormData.collaborators.reduce((acc: any, item) => {
                            acc[item.email] = item.sharePercentage;
                            return acc;
                        }, {})
                        : null,
                        // [{
                        //     id: "owner",
                        //     name: this.rootStore.authStore?.currentUser?.firstName + ' ' + this.rootStore.authStore?.currentUser?.lastName || 'Chủ sở hữu',
                        //     email: this.rootStore.authStore?.currentUser?.email || 'owner@example.com',
                        //     role: "owner",
                        //     sharePercentage: 100
                        // }],

                    protection: this.contentFormData.protection,
                    dimensions: this.contentFormData.dimensions,
                    material: this.contentFormData.material
                };

                const apiConfig = await import('@/api/apiClient');
                const baseURL = apiConfig.default.client?.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:1337';
                const token = localStorage.getItem('token');

                const response = await uploadContentToStrapi(
                    baseURL,
                    token,
                    dataPayload,
                    this.contentFormData.files,
                    (progress) => {
                        runInAction(() => {
                            this.uploadProgress = progress;
                        });
                    }
                );

                // ✅ Update MinIO status từ response
                if (response.files) {
                    runInAction(() => {
                        this.minioUploadStatus.uploadedFiles = response.files.map((file: any) => ({
                            name: file.originalName || file.name,
                            path: file.path || '',
                            url: file.url,
                            size: file.size,
                            mimeType: file.mimeType || file.mime,
                            bucket: file.bucket || 'vietrro-portal'
                        }));
                    });
                }

                runInAction(() => {
                    this.registerSuccess = true;
                    this.isLoading = false;
                    this.uploadProgress = 100;
                });

                return response;

            } catch (apiError: any) {
                console.warn('API registration failed, using mock registration:', apiError.message);

                // Simulate API call với mock data - ✅ Giữ nguyên
                for (let i = 0; i <= 100; i += 10) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    runInAction(() => {
                        this.uploadProgress = i;
                    });
                }

                // Mock response
                const mockResponse = {
                    data: {
                        id: Date.now().toString(),
                        attributes: {
                            title: this.contentFormData.title,
                            description: this.contentFormData.description,
                            contentKind: this.contentFormData.contentKind,
                            category: this.contentFormData.category,
                            status: 'pending',
                            registrationDate: new Date().toISOString(),
                            isPublic: this.contentFormData.isPublic,
                            collaborators: this.contentFormData.collaborators,
                            protection: this.contentFormData.protection
                        }
                    }
                };

                runInAction(() => {
                    this.registerSuccess = true;
                    this.isLoading = false;
                    this.uploadProgress = 100;
                });

                return mockResponse;
            }

        } catch (error: any) {
            console.error('Error registering content:', error);

            runInAction(() => {
                this.error = error.message || 'errorRegisterWorkFailed';
                this.isLoading = false;
                this.uploadProgress = 0;
            });

            throw error;
        }
    }

    // ✅ Giữ nguyên tất cả Contents management methods
    setPage(page: number) {
        this.page = page;
        this.fetchContents();
    }

    setViewMode(mode: 'grid' | 'list') {
        this.viewMode = mode;
    }

    setFilter(key: string, value: string) {
        this.filters = ensureFilters(this.filters);
        // @ts-ignore
        this.filters[key] = value;
        this.page = 1;
    }

    setSort(field: string) {
        this.filters = ensureFilters(this.filters);
        if (this.filters.sortField === field) {
            this.filters.sortOrder = this.filters.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.filters.sortField = field;
            this.filters.sortOrder = 'desc';
        }
        this.page = 1;
        this.fetchContents();
    }

    // ✅ Fetch Contents for Recent Section
    async fetchRecentContents() {
        runInAction(() => {
            this.isLoading = true;
            this.error = null;
        });

        try {
            const response = await contentService.getRecentContents();
            runInAction(() => {
                this.contents = response.data || [];
                this.isLoading = false;
            });
        } catch (error: any) {
            console.warn('❌ ContentStore.fetchContents API failed:', error.message);
            // ✅ Enhanced mock data với user relationship
            // const mockContents = this.generateMockContentsWithUser();

            runInAction(() => {
                // this.contents = mockContents;
                this.contents = [];
                this.error = error.message || 'Fetch Contents error';
                this.isLoading = false;
            });
        }
    }

    // ✅ MINIMAL FIX: Chỉ sửa fetchContents method
    async fetchContents(params?: Record<string, string | number | boolean>) {
        this.isLoading = true;
        this.error = null;

        this.filters = ensureFilters(this.filters);

        try {
            // throw new Error('ABABABBA');
            // ✅ FIX: Gọi getUserContents với error handling tốt hơn
            const response = await contentService.getUserContents(
                this.page,
                this.pageSize,
                params && Object.keys(params).length > 0 
                    ? {...this.filters, ...params} 
                    : this.filters
            );

            runInAction(() => {
                const start = (this.page - 1) * this.pageSize;
                const end = this.page * this.pageSize
                this.contents = response.data.slice(start, end) || []; 
                // response.data.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize) || [];
                this.totalCount = response.pagination?.total || this.contents.length || 0;
                this.isLoading = false;
            });

            // ✅ Log để debug
            if(import.meta.env.MODE === 'development') {
                console.log('✅ ContentStore.fetchContents success:', {
                    page: this.page,
                    pageSize: this.pageSize,
                    totalItems: this.contents.length,
                    totalCount: this.totalCount
                });
            }

        } catch (error: any) {
            // throw error;
            console.warn('❌ ContentStore.fetchContents API failed:', error.message);

            // ✅ Enhanced mock data với user relationship
            const mockContents = this.generateMockContentsWithUser();

            runInAction(() => {
                this.contents = mockContents;
                this.totalCount = 25;
                this.isLoading = false;
            });

            console.log('✅ Using enhanced mock data:', mockContents.length, 'items');
        }
    }

    // ✅ CHỈ SỬA: Enhanced mock data generator
    private generateMockContentsWithUser() {
        const mockContents = [];
        const contentKinds = ['image', 'audio', 'video', 'text', 'other'];
        const statuses = ['approved', 'pending', 'rejected', 'draft'];

        // Get current user info for mock
        const currentUser = this.rootStore.authStore?.currentUser;
        const userId = currentUser?.id || '29';
        const userName = currentUser?.fullName || 'Current User';

        for (let i = 1; i <= this.pageSize; i++) {
            const id = `mock-${this.page}-${i}`;
            const documentId = `doc-${this.page}-${i}-${Date.now()}`;
            const contentKind = contentKinds[Math.floor(Math.random() * contentKinds.length)];
            const status = statuses[Math.floor(Math.random() * statuses.length)];

            mockContents.push({
                id,
                documentId, // ✅ Add documentId for Strapi v5 compatibility
                attributes: {
                    title: `Tác phẩm Mock ${id}`,
                    description: `Mô tả chi tiết cho tác phẩm ${id}`,
                    contentKind,
                    contentStatus: status,
                    registrationDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
                    isPublic: Math.random() > 0.5,
                    thumbnailUrl: `/images/content/content${(i % 5) + 1}.jpg`,
                    category: {
                        data: {
                            id: Math.floor(Math.random() * MOCK_CATEGORIES.length) + 1,
                            attributes: {
                                name: MOCK_CATEGORIES[Math.floor(Math.random() * MOCK_CATEGORIES.length)].name
                            }
                        }
                    },
                    // ✅ Enhanced user relationships
                    generatedBy: {
                        data: {
                            id: userId,
                            attributes: {
                                firstName: userName.split(' ')[0] || 'User',
                                lastName: userName.split(' ')[1] || '',
                                email: currentUser?.email || 'user@example.com'
                            }
                        }
                    },
                    owners: {
                        data: [{
                            id: userId,
                            attributes: {
                                firstName: userName.split(' ')[0] || 'User',
                                lastName: userName.split(' ')[1] || '',
                                email: currentUser?.email || 'user@example.com'
                            }
                        }]
                    },
                    licenseCount: Math.floor(Math.random() * 20),
                    viewCount: Math.floor(Math.random() * 1000),
                    protectionSettings: {
                        watermark: Math.random() > 0.5,
                        aiMonitoring: Math.random() > 0.3,
                        blockchain: Math.random() > 0.7
                    },
                    files: {
                        data: []
                    }
                }
            });
        }

        return mockContents;
    }

    async fetchUserContent({ userId}: {userId?: ID}) {
        this.isLoading = true;
        this.error = null;

        try {
            // Gọi service để lấy contents của user
            const response = await contentService.getUserContents(1, 100, this.filters, userId);
                        console.log("response", response)
            runInAction(() => {
                if(userId) {
                    this.userContent = response.data || [];
                } else {
                    this.contents = response.data || [];
                }
                
                this.isLoading = false;
            });

            // console.log('✅ Fetched user content:', this.userContent.length, 'items');
            return this.userContent;
        } catch (error: any) {
            console.error('Error fetching user content:', error);

            // Fallback với mock data
            const mockUserContent = this.generateMockUserContent();

            runInAction(() => {
                this.userContent = mockUserContent;
                this.isLoading = false;
                this.error = error.message || 'Không thể tải danh sách tác phẩm';
            });

            console.log('⚠️ Using mock user content:', this.userContent.length, 'items');
            return this.userContent;
        }
    }

    /**
     * Generate mock user content cho testing
     * @private
     */
    private generateMockUserContent() {
        const currentUser = this.rootStore.authStore?.currentUser;
        const userId = currentUser?.id || '1';
        const userName = currentUser?.fullName || 'Test User';

        return [
            {
                id: 1,
                documentId: 'doc-1-2025',
                title: "Bài hát Mùa Xuân",
                description: "Sáng tác âm nhạc về mùa xuân",
                contentKind: "audio",
                category: { id: '1', name: 'Âm nhạc' },
                status: 'approved',
                createdAt: '2024-01-15T10:00:00Z',
                user: { id: userId, fullName: userName }
            },
            {
                id: 2,
                documentId: 'doc-2-2025',
                title: "Ảnh Phong Cảnh Đà Lạt",
                description: "Bộ ảnh phong cảnh Đà Lạt mùa hoa dã quỳ",
                contentKind: "image",
                category: { id: '4', name: 'Nhiếp ảnh' },
                status: 'approved',
                createdAt: '2024-02-20T14:30:00Z',
                user: { id: userId, fullName: userName }
            },
            {
                id: 3,
                documentId: 'doc-3-2025',
                title: "Truyện ngắn Cơn Mưa",
                description: "Truyện ngắn về tình yêu và hy vọng",
                contentKind: "text",
                category: { id: '2', name: 'Văn học' },
                status: 'pending',
                createdAt: '2024-03-10T09:15:00Z',
                user: { id: userId, fullName: userName }
            },
            {
                id: 4,
                documentId: 'doc-4-2025',
                title: "Video Hướng Dẫn Nấu Ăn",
                description: "Series video dạy nấu các món ăn Việt Nam",
                contentKind: "video",
                category: { id: '5', name: 'Phim ảnh' },
                status: 'approved',
                createdAt: '2024-03-25T16:45:00Z',
                user: { id: userId, fullName: userName }
            },
            {
                id: 5,
                documentId: 'doc-5-2025',
                title: "Phần mềm Quản lý Thư viện",
                description: "Ứng dụng quản lý thư viện sách điện tử",
                contentKind: "other",
                category: { id: '6', name: 'Phần mềm' },
                status: 'approved',
                createdAt: '2024-04-05T11:20:00Z',
                user: { id: userId, fullName: userName }
            }
        ];
    }

    // ✅ GIỮ NGUYÊN: generateMockContents cũ (để backward compatibility)
    private generateMockContents() {
        const mockContents = [];
        const contentKinds = ['image', 'audio', 'video', 'text', 'other'];
        const statuses = ['approved', 'pending', 'rejected', 'draft'];

        for (let i = 1; i <= this.pageSize; i++) {
            const id = `mock-${this.page}-${i}`;
            const contentKind = contentKinds[Math.floor(Math.random() * contentKinds.length)];
            const status = statuses[Math.floor(Math.random() * statuses.length)];

            mockContents.push({
                id,
                documentId: `doc-${this.page}-${i}-${Date.now()}`, // ✅ Add documentId
                title: `Tác phẩm Mock ${id}`,
                description: `Mô tả chi tiết cho tác phẩm ${id}`,
                contentKind,
                status,
                registrationDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
                isPublic: Math.random() > 0.5,
                thumbnail: `/images/content/content${(i % 5) + 1}.jpg`,
                category: {
                    name: MOCK_CATEGORIES[Math.floor(Math.random() * MOCK_CATEGORIES.length)].name
                },
                owners: [{
                    firstName: 'Nguyễn',
                    lastName: 'Văn A'
                }],
                licenseCount: Math.floor(Math.random() * 20),
                viewCount: Math.floor(Math.random() * 1000),
                protectionScore: Math.floor(Math.random() * 30) + 70,
                files: []
            });
        }

        return mockContents;
    }

    // ✅ UPDATED: Delete content với documentId support (Strapi v5)
    async deleteContent(id: string) {
        try {
            this.isLoading = true;
            console.log(`🗑️ Deleting content: ${id}`);

            // ✅ Use updated contentService with documentId support
            await contentService.deleteContent(id);

            runInAction(() => {
                // Remove từ contents list
                this.contents = this.contents.filter(content => {
                    // Check both id and documentId for compatibility
                    return content.id !== id && content.documentId !== id;
                });
                this.totalCount -= 1;
                this.isLoading = false;
            });

            console.log(`✅ Successfully deleted content: ${id}`);
            return true;
        } catch (error: any) {
            console.warn('API delete failed, using mock delete:', error.message);

            // Mock delete for fallback
            runInAction(() => {
                this.contents = this.contents.filter(content => {
                    return content.id !== id && content.documentId !== id;
                });
                this.totalCount -= 1;
                this.isLoading = false;
            });

            console.log(`✅ Mock deleted content: ${id}`);
            return true;
        }
    }

    // ✅ NEW: Update content với documentId support
    async updateContent(id: string, updateData: any) {
        try {
            this.isLoading = true;
            console.log(`🔄 Updating content: ${id}`);

            // ✅ Use updated contentService with documentId support
            const updatedContent = await contentService.updateContent(id, updateData);

            runInAction(() => {
                // Update trong contents list
                const index = this.contents.findIndex(content =>
                    content.id === id || content.documentId === id
                );

                if (index !== -1) {
                    this.contents[index] = {
                        ...this.contents[index],
                        attributes: {
                            ...this.contents[index].attributes,
                            ...updateData
                        }
                    };
                }

                this.isLoading = false;
            });

            console.log(`✅ Successfully updated content: ${id}`);
            return updatedContent;
        } catch (error: any) {
            console.error('Error updating content:', error);

            runInAction(() => {
                this.error = error.message || 'Cập nhật thất bại';
                this.isLoading = false;
            });

            throw error;
        }
    }

    // ✅ NEW: Bulk operations với documentId support
    async bulkDeleteContents(contentIds: string[]) {
        try {
            this.isLoading = true;
            console.log(`🗑️ Bulk deleting ${contentIds.length} contents`);

            // ✅ Use contentService bulk delete
            const result = await contentService.bulkDeleteContents(contentIds);

            runInAction(() => {
                // Remove successful deletions from store
                const successfulIds = result.results
                    .filter(r => r.success)
                    .map(r => r.id);

                this.contents = this.contents.filter(content =>
                    !successfulIds.includes(content.id) &&
                    !successfulIds.includes(content.documentId)
                );

                this.totalCount -= result.summary.successful;
                this.isLoading = false;
            });

            console.log(`✅ Bulk delete completed: ${result.summary.successful}/${result.summary.total} successful`);
            return result;
        } catch (error: any) {
            console.error('Bulk delete error:', error);

            runInAction(() => {
                this.error = error.message || 'Xóa hàng loạt thất bại';
                this.isLoading = false;
            });

            throw error;
        }
    }

    async bulkUpdateContents(contentIds: string[], updateData: any) {
        try {
            this.isLoading = true;
            console.log(`🔄 Bulk updating ${contentIds.length} contents`);

            // ✅ Use contentService bulk update
            const result = await contentService.bulkUpdateContents(contentIds, updateData);

            runInAction(() => {
                // Update successful updates in store
                const successfulIds = result.results
                    .filter(r => r.success)
                    .map(r => r.id);

                this.contents = this.contents.map(content => {
                    if (successfulIds.includes(content.id) || successfulIds.includes(content.documentId)) {
                        return {
                            ...content,
                            attributes: {
                                ...content.attributes,
                                ...updateData
                            }
                        };
                    }
                    return content;
                });

                this.isLoading = false;
            });

            console.log(`✅ Bulk update completed: ${result.summary.successful}/${result.summary.total} successful`);
            return result;
        } catch (error: any) {
            console.error('Bulk update error:', error);

            runInAction(() => {
                this.error = error.message || 'Cập nhật hàng loạt thất bại';
                this.isLoading = false;
            });

            throw error;
        }
    }

    // ✅ NEW: Get content by ID với documentId support
    async getContentById(id: string) {
        try {
            this.isLoading = true;
            console.log(`🔍 Fetching content by ID: ${id}`);

            const content = await contentService.getContentById(id);

            runInAction(() => {
                this.isLoading = false;
            });

            console.log(`✅ Found content: ${content.id} (documentId: ${content.documentId})`);
            return content;
        } catch (error: any) {
            console.error('Error fetching content by ID:', error);

            runInAction(() => {
                this.error = error.message || 'Không tìm thấy nội dung';
                this.isLoading = false;
            });

            throw error;
        }
    }

    // ✅ Enhanced resetForm với MinIO cleanup
    resetForm() {
        this.contentFormData = {
            title: '',
            description: '',
            contentKind: '',
            category: '',
            files: [],
            tags: [],
            isPublic: false,
            protection: {
                watermark: true,
                aiMonitoring: true,
                blockchain: false
            },
            collaborators: [],
            termsAgreed: false,
            ownershipAgreed: false
        };
        this.currentStep = 1;
        this.registerSuccess = false;
        this.error = null;
        this.uploadProgress = 0;

        // ✅ Reset MinIO status
        this.minioUploadStatus = {
            isUploading: false,
            uploadedFiles: [],
            totalSize: 0
        };
    }

    // ✅ Giữ nguyên getters
    get collaboratorsShareTotal() {
        return this.contentFormData.collaborators.reduce((sum, c) => sum + (Number(c.sharePercentage) || 0), 0);
    }

    get protectionScore() {
        const { watermark, aiMonitoring, blockchain } = this.contentFormData.protection;
        let score = 50;

        if (watermark) score += 15;
        if (aiMonitoring) score += 20;
        if (blockchain) score += 15;

        return score;
    }

    get isFormValid() {
        const { title, contentKind, category, collaborators, termsAgreed, ownershipAgreed } = this.contentFormData;

        const isCollaboratorsValid = collaborators.length === 0 || this.collaboratorsShareTotal === 100;

        if (this.currentStep === 5) {
            return (
                !!title &&
                !!contentKind &&
                !!category &&
                isCollaboratorsValid &&
                termsAgreed === true &&
                ownershipAgreed === true
            );
        }

        return (
            !!title &&
            !!contentKind &&
            !!category &&
            isCollaboratorsValid
        );
    }

    // ✅ Thêm getter mới cho MinIO files info
    get uploadedFilesInfo() {
        return this.minioUploadStatus.uploadedFiles.map(file => ({
            ...file,
            sizeFormatted: (file.size / 1024 / 1024).toFixed(2) + ' MB'
        }));
    }

    // ✅ NEW: Helper để get content identifier (id hoặc documentId)
    getContentIdentifier(content: any): string {
        return content.documentId || content.id;
    }

    // ✅ NEW: Helper để check xem có phải documentId format không
    isDocumentId(identifier: string): boolean {
        return typeof identifier === 'string' &&
            identifier.includes('-') &&
            identifier.length > 10;
    }

    // ✅ NEW: Search contents với documentId support
    async searchContents(searchTerm: string, filters?: any) {
        try {
            this.isLoading = true;
            this.error = null;

            // Set search term in filters
            this.setFilter('searchTerm', searchTerm);

            // Apply additional filters if provided
            if (filters) {
                Object.keys(filters).forEach(key => {
                    this.setFilter(key, filters[key]);
                });
            }

            // Fetch with current filters
            await this.fetchContents();

            console.log(`✅ Search completed for: "${searchTerm}"`);
        } catch (error: any) {
            console.error('Search error:', error);

            runInAction(() => {
                this.error = error.message || 'Tìm kiếm thất bại';
                this.isLoading = false;
            });

            throw error;
        }
    }

    // ✅ NEW: Export/Import content với documentId support
    async exportContentData(contentIds: string[]) {
        try {
            console.log(`📤 Exporting ${contentIds.length} contents`);

            const exportData = [];

            for (const id of contentIds) {
                try {
                    const content = await this.getContentById(id);
                    exportData.push({
                        id: content.id,
                        documentId: content.documentId,
                        attributes: content.attributes,
                        exportedAt: new Date().toISOString()
                    });
                } catch (error) {
                    console.warn(`Failed to export content ${id}:`, error);
                }
            }

            const exportFile = {
                metadata: {
                    exportedAt: new Date().toISOString(),
                    totalContents: exportData.length,
                    strapiVersion: 'v5',
                    vietrroPortalVersion: '1.0'
                },
                contents: exportData
            };

            // Create and download file
            const blob = new Blob([JSON.stringify(exportFile, null, 2)], {
                type: 'application/json'
            });

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vietrro-contents-export-${Date.now()}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            console.log(`✅ Exported ${exportData.length} contents successfully`);
            return exportFile;
        } catch (error: any) {
            console.error('Export error:', error);
            throw error;
        }
    }

    // ✅ THÊM method mới cho homepage
    async fetchAllContents(params: {
        page?: number;
        pageSize?: number;
        populate?: string[];
        sort?: string;
    } = {}) {
        this.setLoading(true);
        this.setError(null);

        try {
            const response = await contentService.getAllApprovedContent({
                limit: params.pageSize || 4,
                page: params.page || 1
            });

            runInAction(() => {
                this.contents = response.data || [];
                this.totalCount = response.meta?.pagination?.total || 0;
                this.isLoading = false;
            });

            console.log('✅ ContentStore.fetchAllContents success:', {
                totalItems: this.contents.length,
                totalCount: this.totalCount
            });

        } catch (error) {
            // Handle error with mock fallback
        }
    }

    /**
 * Set contents data directly (for external API calls)
 */
    setContents(contents: any[], pagination?: {
        total: number;
        page: number;
        pageSize: number;
        pageCount: number;
    }) {
        runInAction(() => {
            this.contents = contents;
            this.totalCount = pagination?.total || contents.length;

            if (pagination) {
                this.page = pagination.page;
                this.pageSize = pagination.pageSize;
            }

            this.isLoading = false;
            this.error = null;
        });

        // console.log('✅ ContentStore.setContents:', {
        //     contentsCount: contents.length,
        //     totalCount: this.totalCount,
        //     page: this.page,
        //     pageSize: this.pageSize
        // });
    }
}

export default ContentStore;
